import React, { useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import Joyride, { EVENTS, StoreHelpers } from "react-joyride";
import {
  TutorialJourneyStatus,
  TutorialJourneyType,
} from "@growth-machine-llc/stridist-api-client";
import {
  JOURNEY_UI_CONFIG,
  STEP_UI_CONFIG,
  TUTORIAL_JOURNEY_ANCHORS,
} from "../../../constants";
import { getCookie } from "../../../components/program-component/utils";
import { getJourneyTypeForPath } from "../utils";
import { useJourneyWithActivator } from "../../../components/app/hooks/useJourneyWithActivator";
import JourneyCustomTooltip from "../../../components/admin/journey/JourneyCustomTooltip";
import { JourneyRunContext } from "../../../contexts/JourneyRunContext";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { useJoyrideCallback } from "../hooks/useJoyrideCallback";

export const JourneyRouteManager = ({
  autoStart = false,
  onFinishJourney = () => {},
}) => {
  const { pathname, search } = useLocation();
  const journeyType = useMemo(
    () => getJourneyTypeForPath(pathname, search),
    [pathname, search],
  );
  const user = useCurrentUser();
  // Force-show only when cookie is set AND user is admin
  const devCookie = user.admin && getCookie("tutorials_enabled") === "true";

  const journeyUI = JOURNEY_UI_CONFIG[journeyType];
  const stepUIMap = STEP_UI_CONFIG[journeyType];
  const helpersRef = React.useRef<StoreHelpers | null>(null);
  const detachClickRef = React.useRef<() => void>();

  const { isForced, isRunning, setIsForced, setIsRunning } =
    useContext(JourneyRunContext);

  const { steps, ActivatorDialog, runJourney, setRunJourney, journeyStatus } =
    useJourneyWithActivator(
      journeyType as TutorialJourneyType,
      isForced,
      devCookie,
    );

  useEffect(() => {
    if (runJourney) {
      setIsRunning(true);
    }
  }, [runJourney]);

  useEffect(() => {
    if (isForced) {
      requestAnimationFrame(() => {
        setRunJourney(true);
        setIsRunning(true);
        setIsForced(false);
      });
    }
  }, [isForced, setIsForced, setRunJourney, setIsRunning]);

  const hasSpotlightClicks = useMemo(
    () =>
      stepUIMap
        ? Object.values(stepUIMap).some((s) => s.spotlightClicks)
        : false,
    [stepUIMap],
  );

  const updatedSteps = React.useMemo(() => {
    return steps.map((step, index) => {
      const meta = step.meta as { anchor?: string; profile: any } | undefined;
      let target = "";
      if (meta?.anchor) {
        const mappingValue = TUTORIAL_JOURNEY_ANCHORS[journeyType][meta.anchor];
        if (mappingValue) {
          target = mappingValue;
        }
      }
      if (target && !target.startsWith(".")) {
        target = `.${target}`;
      }
      const override = stepUIMap[meta.anchor] ?? {};

      return {
        ...step,
        target,
        isFixed: override.isFixed,
        disableScrolling:
          override.disableScrolling ?? journeyUI.disableScrolling,
        scrollOffset: override.scrollOffset ?? journeyUI.scrollOffset,
        spotlightClicks: override.spotlightClicks ?? false,
      };
    });
  }, [steps, journeyType]);

  const joyrideCallback = useJoyrideCallback(
    updatedSteps,
    helpersRef,
    detachClickRef,
    onFinishJourney,
    setRunJourney,
    setIsRunning,
    setIsForced,
    journeyType,
  );

  if (!journeyType) return null;
  if (journeyStatus === TutorialJourneyStatus.DRAFT && !user.admin) return null;
  if (!user.subscription) {
    return null;
  }

  return (
    <>
      {!autoStart && !runJourney && ActivatorDialog}
      {runJourney && updatedSteps.length > 0 && (
        <Joyride
          steps={updatedSteps}
          run={runJourney}
          continuous
          showSkipButton
          showProgress
          disableOverlayClose
          spotlightClicks={hasSpotlightClicks}
          scrollOffset={journeyUI.scrollOffset}
          disableScrolling={journeyUI.disableScrolling}
          styles={{ options: { zIndex: 100000 } }}
          tooltipComponent={(props: any) => {
            const { profile } = props.step?.meta || {};
            return (
              <JourneyCustomTooltip
                {...props}
                stepIndex={props.index}
                totalSteps={updatedSteps.length}
                profileName={profile?.name || ""}
                profileAvatar={profile?.avatar || ""}
                profileSubtitle={profile?.subtitle}
                showProfileInfo={!!profile?.name && !!profile?.avatar}
              />
            );
          }}
          getHelpers={(h) => {
            helpersRef.current = h;
          }}
          callback={joyrideCallback}
        />
      )}
    </>
  );
};
